import { useRef, useState } from "react";
import { UploadCloud, FileText, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";

const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";
const MAX_MB = 20;

function ext(name = "") {
  return name.split(".").pop()?.toLowerCase() || "";
}

export default function AttachmentDropzone({ attachments, onChange, testid = "dropzone" }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const successes = [];
    for (const f of Array.from(files)) {
      if (f.size > MAX_MB * 1024 * 1024) {
        toast.error(`${f.name} exceeds ${MAX_MB} MB.`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/attachments", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        successes.push(data);
      } catch (e) {
        toast.error(`${f.name}: ${formatApiError(e)}`);
      }
    }
    setUploading(false);
    if (successes.length > 0) {
      onChange([...(attachments || []), ...successes]);
      toast.success(`Uploaded ${successes.length} file${successes.length > 1 ? "s" : ""}.`);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    upload(e.dataTransfer.files);
  };

  const remove = (id) => {
    onChange((attachments || []).filter((a) => a.id !== id));
  };

  return (
    <div data-testid={testid}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragging ? "border-[#4FA3D1] bg-sky-50" : "border-slate-300 bg-slate-50/50 hover:bg-slate-50"
        }`}
        data-testid={`${testid}-drop`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          onChange={(e) => upload(e.target.files)}
          className="hidden"
          data-testid={`${testid}-input`}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2 py-4">
            <Loader2 className="h-7 w-7 animate-spin text-[#4FA3D1]" />
            <p className="text-sm text-slate-600">Uploading…</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-4">
            <UploadCloud className="h-9 w-9 text-[#4FA3D1]" />
            <p className="text-sm font-medium text-slate-700">
              Drop PDF / JPG / PNG / WEBP here, or click to browse
            </p>
            <p className="text-xs text-slate-500">Max {MAX_MB} MB per file</p>
          </div>
        )}
      </div>

      {(attachments || []).length > 0 && (
        <ul className="mt-3 space-y-2" data-testid={`${testid}-list`}>
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2 bg-white"
              data-testid={`${testid}-item-${a.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="h-5 w-5 text-slate-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{a.original_name}</p>
                  <p className="text-xs text-slate-500">
                    {(a.size_bytes / 1024).toFixed(1)} KB · {ext(a.original_name).toUpperCase()}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(a.id)}
                data-testid={`${testid}-remove-${a.id}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

import { downloadAttachment as _download } from "@/lib/downloadAttachment";

// P0 (2026-07-14) — legacy sync helper. Callers should migrate to the
// blob-download flow (`downloadAttachment(id, filename)` from
// `@/lib/downloadAttachment`) because plain URLs no longer authenticate.
// This wrapper stays only for existing `<img src=…>` inline previews
// that need a URL synchronously; we set it to null so anchor-based
// callers see a broken link (and can migrate).
export function attachmentPreviewUrl(_att) { return null; }
export { _download as downloadAttachment };
